import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  Building2,
  AlertTriangle,
  ArrowRight,
  MapPin,
  Trash2,
  AlertCircle,
  Info,
  ShieldAlert
} from "lucide-react";

const ZeroBranchStock = () => {
  const [branches, setBranches] = useState([]);
  const { requestWithAuth } = useAuth();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      toast.error("Failed to load branches");
    }
  };

  const handleZeroStock = async () => {
    if (!selectedBranch) return;

    const branchName = branches.find(b => b.id == selectedBranch)?.name || "the selected branch";

    Swal.fire({
      title: "Are you absolutely sure?",
      text: `This action will set the inventory for ${branchName} to zero, delete all drug batches and that the action is irreversible.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsProcessing(true);
        try {
          const data = await requestWithAuth(
            `/inventory/admin/clear-branch-stock/${selectedBranch}/`,
            "POST"
          );

          Swal.fire({
            icon: "success",
            title: "Action Completed",
            html: `
              <div class="text-left mt-2">
                <p class="text-sm font-medium text-gray-700">${data?.detail || "Branch stock quantities set to zero."}</p>
                <div class="mt-4 space-y-2">
                  <div class="flex justify-between text-xs py-1 border-b">
                    <span>Updated Items:</span>
                    <span class="font-bold">${data?.updated_items || 0}</span>
                  </div>
                  <div class="flex justify-between text-xs py-1">
                    <span>Deleted Batches:</span>
                    <span class="font-bold text-red-600">${data?.deleted_batches || 0}</span>
                  </div>
                </div>
              </div>
            `,
          });
          
          setSelectedBranch(null);
        } catch (error) {
          console.error("Error zeroing stock:", error);
          const errorMsg = error.response?.data?.detail || "An error occurred while processing the request.";
          Swal.fire({
            icon: "error",
            title: "Request Failed",
            text: errorMsg,
          });
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Zero Branch Stock
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ShieldAlert size={32} className="text-red-600" />
                Zero Branch Stock
              </h1>
              <p className="text-gray-600 mt-1">Clear all quantities and batches for a specific branch</p>
            </div>
          </div>
        </div>

        {/* Main Content - Centered Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Card Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-rose-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Trash2 className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Configuration
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Select the branch to perform the zeroing operation
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Warning Banner */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-lg shrink-0">
                      <AlertTriangle size={20} className="text-red-700 font-bold" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-red-900">
                        Critical Warning: Irreversible Action
                      </p>
                      <p className="text-xs text-red-700 mt-1 font-medium leading-relaxed">
                        This operation will set ALL inventory levels to 0 and permanently DELETE all current drug batches for the target branch. 
                        Data cannot be recovered once this process completes.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Branch Selection */}
                <div>
                  <label
                    htmlFor="branch-select"
                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
                  >
                    <MapPin size={16} className="text-gray-500" />
                    Target Branch
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="branch-select"
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    value={selectedBranch || ""}
                    disabled={isProcessing}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white transition-all disabled:opacity-50"
                  >
                    <option value="">Choose a branch to zero out...</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} {branch.is_head_office ? "(Head Office)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Requirements Info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                      <Info size={18} className="text-gray-600" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-800">Operation Manifest:</p>
                      <ul className="text-xs text-gray-600 space-y-1 list-disc ml-4">
                        <li>All <span className="font-semibold text-gray-900">Stock Quantities</span> will be set to 0.00</li>
                        <li>All <span className="font-semibold text-gray-900">Batch Records</span> will be permanently deleted.</li>
                        <li>Inventory history for the branch will be effectively cleared.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleZeroStock}
                  disabled={!selectedBranch || isProcessing}
                  className={`w-full rounded-xl py-4 font-bold transition-all shadow-md flex items-center justify-center gap-3 text-lg ${
                    selectedBranch && !isProcessing
                      ? "bg-red-600 hover:bg-red-700 text-white hover:shadow-lg active:scale-[0.98]"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing Zeroing...
                    </>
                  ) : (
                    <>
                      Zero Branch Inventory
                      <ArrowRight size={22} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Safeguard Info */}
        <div className="max-w-2xl mx-auto mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex gap-4">
            <div className="bg-blue-50 p-2.5 rounded-lg h-fit">
              <AlertCircle size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Double Confirmation</p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                We'll ask for your confirmation one last time before performing the deletion.
              </p>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex gap-4">
            <div className="bg-amber-50 p-2.5 rounded-lg h-fit">
              <Building2 size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Branch Scoped</p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                This operation strictly targets the selected branch only. No other branches are affected.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZeroBranchStock;
